import React, { useEffect, useState, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { resolveToken } from '../../../shared/marketing/themeTokens';
import useCrmSearch from '../../shared/components/useCrmSearch';
import CrmSearchResults from '../../shared/components/CrmSearchResults';
import crmLabels from '../../shared/utils/crmLabels';

const ICON_STROKE = resolveToken('--sl-text-quaternary', '#717680');
const ICON_STROKE_FAINT = resolveToken('--sl-text-disabled', '#A4A7AE');

const ContactsSidebar = ({ team, activeKey = 'contacts', onToggleSidebar }) => {
  const teamId = team && team.id;
  const labels = crmLabels(team);

  const menuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      href: teamId ? `/teams/${teamId}/crm_dashboard` : '#',
      icon: (
        <svg
          className="sprite-icon figma-icon secondary"
          width="20"
          height="20"
          aria-hidden="true"
        >
          <use xlinkHref="#dashboard-grid" />
        </svg>
      ),
    },
    {
      key: 'contacts',
      label: labels.contact.plural,
      href: teamId ? `/teams/${teamId}/crm/fans` : '#',
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M14.9999 13.1974C16.2131 13.8069 17.2533 14.785 18.0126 16.008C18.1629 16.2502 18.2381 16.3713 18.2641 16.539C18.3169 16.8798 18.0839 17.2988 17.7665 17.4336C17.6103 17.5 17.4346 17.5 17.0832 17.5M13.3332 9.6102C14.568 8.99657 15.4165 7.72238 15.4165 6.25C15.4165 4.77762 14.568 3.50343 13.3332 2.8898M11.6665 6.25C11.6665 8.32107 9.9876 10 7.91653 10C5.84546 10 4.16653 8.32107 4.16653 6.25C4.16653 4.17893 5.84546 2.5 7.91653 2.5C9.9876 2.5 11.6665 4.17893 11.6665 6.25ZM2.13256 15.782C3.46115 13.7871 5.55767 12.5 7.91653 12.5C10.2754 12.5 12.3719 13.7871 13.7005 15.782C13.9916 16.219 14.1371 16.4375 14.1203 16.7166C14.1073 16.9339 13.9648 17.2 13.7912 17.3313C13.5681 17.5 13.2614 17.5 12.6479 17.5H3.18516C2.57167 17.5 2.26493 17.5 2.0419 17.3313C1.86824 17.2 1.72577 16.9339 1.71273 16.7166C1.69597 16.4375 1.8415 16.219 2.13256 15.782Z"
            stroke={ICON_STROKE}
            strokeWidth="1.67"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      key: 'organizations',
      label: labels.organization.plural,
      href: teamId ? `/teams/${teamId}/crm/organizations` : '#',
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M10.8332 9.16667H14.8332C15.7666 9.16667 16.2333 9.16667 16.5898 9.34832C16.9034 9.50811 17.1584 9.76308 17.3182 10.0767C17.4998 10.4332 17.4998 10.8999 17.4998 11.8333V17.5M10.8332 17.5V5.16667C10.8332 4.23325 10.8332 3.76654 10.6515 3.41002C10.4917 3.09641 10.2368 2.84144 9.92316 2.68166C9.56664 2.5 9.09993 2.5 8.1665 2.5H5.1665C4.23308 2.5 3.76637 2.5 3.40985 2.68166C3.09625 2.84144 2.84128 3.09641 2.68149 3.41002C2.49984 3.76654 2.49984 4.23325 2.49984 5.16667V17.5M18.3332 17.5H1.6665M5.4165 5.83333H7.9165M5.4165 9.16667H7.9165M5.4165 12.5H7.9165"
            stroke={ICON_STROKE}
            strokeWidth="1.67"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      key: 'tasks',
      label: 'Tasks',
      href: teamId ? `/teams/${teamId}/crm/tasks` : '#',
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 32 32"
          fill="none"
          aria-hidden="true"
        >
          <line
            x1="15"
            y1="8"
            x2="28"
            y2="8"
            stroke={ICON_STROKE}
            strokeWidth="1.67"
            strokeMiterlimit="10"
          />
          <polyline
            points="5,8 7,10 11,6"
            fill="none"
            stroke={ICON_STROKE}
            strokeWidth="1.67"
            strokeMiterlimit="10"
          />
          <line
            x1="15"
            y1="16"
            x2="28"
            y2="16"
            stroke={ICON_STROKE}
            strokeWidth="1.67"
            strokeMiterlimit="10"
          />
          <polyline
            points="5,16 7,18 11,14"
            fill="none"
            stroke={ICON_STROKE}
            strokeWidth="1.67"
            strokeMiterlimit="10"
          />
          <line
            x1="15"
            y1="24"
            x2="28"
            y2="24"
            stroke={ICON_STROKE}
            strokeWidth="1.67"
            strokeMiterlimit="10"
          />
          <polyline
            points="5,24 7,26 11,22"
            fill="none"
            stroke={ICON_STROKE}
            strokeWidth="1.67"
            strokeMiterlimit="10"
          />
        </svg>
      ),
      submenu: [
        {
          key: 'all_tasks',
          label: 'All Tasks',
          href: teamId ? `/teams/${teamId}/crm/tasks` : '#',
        },
        {
          key: 'my_tasks',
          label: 'My Tasks',
          href: teamId ? `/teams/${teamId}/crm/tasks?view=my` : '#',
        },
      ],
    },
    {
      key: 'deals',
      label: labels.deal.plural,
      href: teamId ? `/teams/${teamId}/crm/deals` : '#',
      icon: (
        <svg
          className="sprite-icon figma-icon secondary"
          width="20"
          height="20"
          aria-hidden="true"
        >
          <use xlinkHref="#deals" />
        </svg>
      ),
    },
    {
      key: 'grants',
      label: 'Grants',
      href: teamId ? `/teams/${teamId}/crm/grants` : '#',
      icon: (
        <svg
          className="sprite-icon figma-icon secondary"
          width="20"
          height="20"
          aria-hidden="true"
        >
          <use xlinkHref="#grants" />
        </svg>
      ),
    },
    {
      key: 'segments',
      label: 'Segments',
      href: teamId ? `/teams/${teamId}/segments` : '#',
      icon: (
        <svg
          className="sprite-icon figma-icon secondary"
          width="20"
          height="20"
          aria-hidden="true"
        >
          <use xlinkHref="#segments" />
        </svg>
      ),
    },
  ].filter((item) => {
    if (item.key === 'deals') return (team && team.deals_enabled) !== false;
    if (item.key === 'grants') return (team && team.grants_enabled) !== false;
    return true;
  });

  const getInitialExpandedKey = () => {
    const parentWithSubmenu = menuItems.find((item) => {
      if (!item.submenu) return false;
      if (item.key === activeKey) return true;
      return item.submenu.some((subItem) => subItem.key === activeKey);
    });

    return parentWithSubmenu ? parentWithSubmenu.key : null;
  };

  const [expandedItemKey, setExpandedItemKey] = useState(getInitialExpandedKey);
  const search = useCrmSearch(teamId);
  const searchInputRef = useRef(null);

  useHotkeys('meta+s', (e) => {
    e.preventDefault();
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, { enableOnFormTags: true });

  useEffect(() => {
    const parentWithSubmenu = menuItems.find((item) => {
      if (!item.submenu) return false;
      if (item.key === activeKey) return true;
      return item.submenu.some((subItem) => subItem.key === activeKey);
    });

    setExpandedItemKey(parentWithSubmenu ? parentWithSubmenu.key : null);
  }, [activeKey]);

  const isItemActive = (item) => {
    if (item.key === activeKey) return true;
    if (item.submenu) {
      return item.submenu.some((subItem) => subItem.key === activeKey);
    }
    return false;
  };

  const isSubItemActive = (subItem) => subItem.key === activeKey;

  const isExpanded = (key) => expandedItemKey === key;

  const toggleExpanded = (key) => {
    setExpandedItemKey((current) => (current === key ? null : key));
  };

  return (
    <nav
      aria-label="Contacts workspace navigation"
      className="contacts-sidebar-nav h-100 d-flex flex-column"
    >
      <div className="contacts-sidebar-header d-flex align-items-center justify-content-between">
        <span>CRM</span>
        {onToggleSidebar && (
          <button
            type="button"
            className="btn btn-sm d-inline-flex align-items-center justify-content-center"
            onClick={onToggleSidebar}
            aria-label="Hide sidebar"
            style={{ padding: '2px 6px', background: 'none', border: 'none' }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M13.3332 11.3333H2.6665M2.6665 11.3333L5.33317 14M2.6665 11.3333L5.33317 8.66667M13.3332 4.66667H5.99984M5.99984 4.66667L8.6665 7.33333M5.99984 4.66667L8.6665 2"
                stroke={ICON_STROKE_FAINT}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      <div className="contacts-sidebar-search-wrapper">
        <div className="contacts-sidebar-search">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
            className="contacts-sidebar-search-icon"
          >
            <path
              d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
              stroke={ICON_STROKE}
              strokeWidth="1.67"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <input
            type="text"
            className="contacts-sidebar-search-input"
            placeholder="Search..."
            value={search.q}
            onChange={(e) => search.setQ(e.target.value)}
            onKeyDown={search.onKeyDown}
            onBlur={() => setTimeout(() => search.setOpen(false), 200)}
            onFocus={() => { if (search.term) search.setOpen(true); }}
            ref={searchInputRef}
          />
          {search.q ? (
            <button
              type="button"
              className="contacts-sidebar-search-clear"
              onClick={() => { search.setQ(''); search.setOpen(false); }}
              aria-label="Clear search"
            >
              <i className="fal fa-times" />
            </button>
          ) : (
            <span className="contacts-sidebar-search-shortcut">⌘S</span>
          )}
        </div>

        {search.open && search.term && (
          <CrmSearchResults
            data={search.data}
            loading={search.loading}
            term={search.term}
            teamId={teamId}
            active={search.active}
            setActive={search.setActive}
            go={search.go}
            seeAllHref={search.seeAllHref}
            labels={labels}
          />
        )}
      </div>

      <ul className="contacts-sidebar-menu list-unstyled mb-3">
        {menuItems.map((item) => {
          const itemIsActive = isItemActive(item);
          const hasSubmenu = Array.isArray(item.submenu) && item.submenu.length > 0;

          return (
            <li key={item.key} className="contacts-sidebar-item">
              <a
                href={item.href}
                className={
                  'contacts-sidebar-link d-flex align-items-center justify-content-between text-decoration-none' +
                  (itemIsActive ? ' contacts-sidebar-link--active' : '')
                }
                aria-expanded={hasSubmenu ? isExpanded(item.key) : undefined}
                aria-controls={hasSubmenu ? `${item.key}-submenu` : undefined}
              >
                <span className="d-flex align-items-center">
                  <span className="contacts-sidebar-icon d-inline-flex align-items-center justify-content-center">
                    {item.icon}
                  </span>
                  <span className="contacts-sidebar-label">{item.label}</span>
                  {item.comingSoon && (
                    <span className="contacts-sidebar-coming-soon">
                      Coming Soon!
                    </span>
                  )}
                </span>

                {hasSubmenu && (
                  <span
                    className="contacts-sidebar-segments-caret"
                    aria-hidden="true"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      toggleExpanded(item.key);
                    }}
                  >
                    <i
                      className={`fal fa-chevron-${isExpanded(item.key) ? 'up' : 'down'}`}
                    />
                  </span>
                )}
              </a>

              {hasSubmenu && isExpanded(item.key) && (
                <ul
                  id={`${item.key}-submenu`}
                  className="contacts-sidebar-segments list-unstyled mt-1"
                >
                  {item.submenu.map((subItem) => (
                    <li key={subItem.key} className="contacts-sidebar-segment-item">
                      <a
                        href={subItem.href}
                        className={
                          'contacts-sidebar-segment-link d-flex align-items-center text-decoration-none' +
                          (isSubItemActive(subItem) ? ' contacts-sidebar-link--active' : '')
                        }
                      >
                        <span className="contacts-sidebar-label">{subItem.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      <div className="contacts-sidebar-settings">
        <a
          href={teamId ? `/teams/${teamId}/crm-settings` : '#'}
          className={
            'contacts-sidebar-link d-flex align-items-center text-decoration-none' +
            (activeKey === 'settings' ? ' contacts-sidebar-link--active' : '')
          }
        >
          <span className="contacts-sidebar-icon d-inline-flex align-items-center justify-content-center">
            <svg
              className="sprite-icon figma-icon secondary"
              width="20"
              height="20"
              aria-hidden="true"
            >
              <use xlinkHref="#settings" />
            </svg>
          </span>
          <span className="contacts-sidebar-label">Settings</span>
        </a>
      </div>
    </nav>
  );
};

export default ContactsSidebar;
